import type { MealPlan, PlannedMeal, Recipe, DayOfWeek } from '../types';
import { ingredientQuantityToGrams } from './meatCalculator';

export interface RecipeUsageEntry {
    count: number;
    name: string;
    totalServings: number;
}

export function aggregateRecipeUsage(
    meals: readonly PlannedMeal[],
    recipes: readonly Recipe[],
): RecipeUsageEntry[] {
    const usage: Record<string, RecipeUsageEntry> = {};

    meals.forEach((meal) => {
        const recipe = recipes.find((entry) => entry.id === meal.recipeId);
        const name = recipe?.name || meal.recipeName;
        if (!name || name === 'Unknown') return;

        if (!usage[name]) {
            usage[name] = { count: 0, name, totalServings: 0 };
        }
        usage[name].count += 1;
        usage[name].totalServings += meal.plannedServings;
    });

    return Object.values(usage).sort((a, b) => b.count - a.count);
}

export function calculateMeatGramsFromPlannedMeals(
    meals: readonly PlannedMeal[],
    recipes: readonly Recipe[],
): number {
    let totalGrams = 0;

    meals.forEach((meal) => {
        const recipe = recipes.find((entry) => entry.id === meal.recipeId);
        if (!recipe) return;

        const ratio = meal.plannedServings / (recipe.servings > 0 ? recipe.servings : 1);
        recipe.ingredients.forEach((ingredient) => {
            if (!ingredient.isMeat) return;
            totalGrams += ingredientQuantityToGrams(ingredient.quantity * ratio, ingredient.unit);
        });
    });

    return Math.round(totalGrams * 10) / 10;
}

export function collectMealsFromPlan(
    mealPlan: MealPlan,
    days: readonly DayOfWeek[],
): PlannedMeal[] {
    return days.flatMap((day) => mealPlan[day] || []);
}
